// Utilities for generating, merging, and diffing channel lists from a stage plot.

import { CHANNEL_DEFAULTS, MicType, SuppliedBy } from '../constants/channelDefaults';

const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export interface ChannelEntry {
  id: string;               // stable UUID for this row (never changes across saves)
  channelNumber: number;    // 1-based, user-reorderable
  elementInstanceId: string;// links to PlacedElement.instanceId; 'manual' for hand-added rows
  elementId: string;        // e.g. 'grand-piano'; 'manual' for hand-added rows
  elementName: string;      // display label, e.g. "Grand Piano"
  channelName: string;      // fully user-editable
  micType: MicType;
  specificMic: string;      // e.g. "DPA 4099" — empty string if not specified
  suppliedBy: SuppliedBy;
  notes: string;
  isOverridden: boolean;    // true once user edits any field away from defaults
}

interface PlacedElementStub {
  instanceId: string;
  id: string;         // instrument/element id
  name: string;       // display label
  x: number;         // used for left→right ordering
}

// Generate a fresh channel list from the current placed elements.
// Elements are ordered left→right by X position (matching how a sound engineer reads a stage).
// Equipment items (amps, monitors, etc.) that have no channel entry are skipped silently.
export function generateChannelList(elements: PlacedElementStub[]): ChannelEntry[] {
  const sorted = [...elements].sort((a, b) => a.x - b.x);

  const rows: ChannelEntry[] = [];
  let chNum = 1;

  for (const el of sorted) {
    const def = CHANNEL_DEFAULTS[el.id];
    if (!def) continue;                      // equipment, risers, labels — skip
    if (def.channels.length === 0) continue; // custom with no defaults — also skip for now

    for (const ch of def.channels) {
      rows.push({
        id: uid(),
        channelNumber: chNum++,
        elementInstanceId: el.instanceId,
        elementId: el.id,
        elementName: el.name,
        channelName: ch.channelName,
        micType: ch.micType,
        specificMic: ch.typicalMic ?? '',
        suppliedBy: ch.suppliedBy,
        notes: def.notes ? def.notes : (ch.notes ?? ''),
        isOverridden: false,
      });
    }
  }

  return rows;
}

// Merge a newly generated list with the existing saved list.
// Rules:
//  - Rows whose elementInstanceId still exists in the new list: keep user overrides, update channelNumber.
//  - Rows whose elementInstanceId is gone (element removed from stage): flag as orphaned (prefixed ⚠).
//  - New rows from elements that weren't in the old list: append.
//  - Manual rows (elementInstanceId === 'manual'): always kept, appended at the end.
export function mergeChannelList(
  existing: ChannelEntry[],
  fresh: ChannelEntry[],
): ChannelEntry[] {
  const freshByInstance = new Map<string, ChannelEntry[]>();
  for (const row of fresh) {
    const key = row.elementInstanceId;
    if (!freshByInstance.has(key)) freshByInstance.set(key, []);
    freshByInstance.get(key)!.push(row);
  }

  const existingInstanceIds = new Set(existing.map(r => r.elementInstanceId));
  const freshInstanceIds = new Set(fresh.map(r => r.elementInstanceId));

  const result: ChannelEntry[] = [];
  let chNum = 1;

  // 1. Walk the fresh list in order — preserve overrides where a matching row exists
  for (const freshRow of fresh) {
    const existingRows = existing.filter(
      e => e.elementInstanceId === freshRow.elementInstanceId && e.elementId === freshRow.elementId,
    );

    // Try to match by channelName (same default name = same physical channel)
    const match = existingRows.find(e => e.channelName === freshRow.channelName || e.isOverridden);
    if (match) {
      result.push({ ...match, channelNumber: chNum++ });
    } else {
      result.push({ ...freshRow, channelNumber: chNum++ });
    }
  }

  // 2. Append orphaned rows (element was removed from stage) with a warning prefix
  for (const row of existing) {
    if (row.elementInstanceId === 'manual') continue; // handled below
    if (!freshInstanceIds.has(row.elementInstanceId)) {
      result.push({
        ...row,
        channelNumber: chNum++,
        channelName: `⚠ ${row.channelName}`,
        notes: `[Element removed from stage] ${row.notes}`.trim(),
      });
    }
  }

  // 3. Always keep manual rows at the end
  const manualRows = existing.filter(r => r.elementInstanceId === 'manual');
  for (const row of manualRows) {
    result.push({ ...row, channelNumber: chNum++ });
  }

  return result;
}

// Return a human-readable diff describing what changed between two channel lists.
// Used to warn the artist before a regenerate operation.
export interface ChannelDiff {
  added: string[];    // element names that will add new channels
  removed: string[];  // element names whose channels will be orphaned
  unchanged: number;  // count of rows unaffected
}

export function diffChannelList(
  existing: ChannelEntry[],
  fresh: ChannelEntry[],
): ChannelDiff {
  const existingIds = new Set(existing.map(r => r.elementInstanceId));
  const freshIds = new Set(fresh.map(r => r.elementInstanceId));

  const added = [...freshIds]
    .filter(id => !existingIds.has(id))
    .map(id => fresh.find(r => r.elementInstanceId === id)?.elementName ?? id)
    .filter((v, i, a) => a.indexOf(v) === i); // dedupe

  const removed = [...existingIds]
    .filter(id => id !== 'manual' && !freshIds.has(id))
    .map(id => existing.find(r => r.elementInstanceId === id)?.elementName ?? id)
    .filter((v, i, a) => a.indexOf(v) === i);

  const unchanged = existing.filter(r => freshIds.has(r.elementInstanceId)).length;

  return { added, removed, unchanged };
}

// Create a blank manual channel row (for the "Add Custom Channel" button).
export function createManualChannel(channelNumber: number): ChannelEntry {
  return {
    id: uid(),
    channelNumber,
    elementInstanceId: 'manual',
    elementId: 'manual',
    elementName: 'Custom',
    channelName: '',
    micType: 'Dynamic',
    specificMic: '',
    suppliedBy: 'venue',
    notes: '',
    isOverridden: true,
  };
}
